import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { uploadUnifiedSweep } from '../../../helpers/api/UnifiedUpload/unified';
import type { UploadSessionByStudy, UploadSessionItem } from '../../../helpers/api/UnifiedUpload/unified';

const VIDEO_EXT = /\.(mp4|webm|mov|avi)$/i;

export interface CompleteIncompleteUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  session: UploadSessionByStudy | null;
  /** When study has multiple incomplete BLIND series, let user pick which one to complete */
  allSessions?: UploadSessionItem[];
  selectedIndex?: number;
  onSelectSession?: (index: number) => void;
}

export const CompleteIncompleteUploadModal: React.FC<CompleteIncompleteUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  session,
  allSessions = [],
  selectedIndex = 0,
  onSelectSession,
}) => {
  const { t } = useTranslation('NewCaseWizard');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const missingKeys = session?.missing_keys && Array.isArray(session.missing_keys) ? session.missing_keys : [];
  const missingCount = missingKeys.length;
  const category = (session?.study_category as 'T1' | 'T2' | 'T3') || 'T1';
  const hasMultipleSeries = allSessions.length > 1;

  useEffect(() => {
    if (isOpen && session) {
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen, session?.upload_session_id, selectedIndex]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setSelectedFiles(files);
  };

  const canSubmit = missingCount > 0 && selectedFiles.length === missingCount;

  const handleSubmit = async () => {
    if (!session || missingCount === 0) {
      onClose();
      return;
    }
    if (selectedFiles.length !== missingCount) {
      messageI18n.warning('upload.selectExactlyFiles', { count: missingCount });
      return;
    }
    setUploading(true);
    const failed: string[] = [];
    try {
      for (let i = 0; i < missingKeys.length; i++) {
        const fileKey = missingKeys[i];
        const file = selectedFiles[i];
        if (!file) continue;
        try {
          const sweepIndex = i + 1;
          const payload = {
            upload_session_id: session.upload_session_id,
            file_key: fileKey,
            examType: 'BLIND' as const,
            studyCategory: category,
            ...(VIDEO_EXT.test(file.name)
              ? {
                  video_meta: {
                    file_name: file.name,
                    width: null,
                    height: null,
                    duration: null,
                    pixel_spacing_value: '0.2,0.2',
                    pixel_spacing_unit: 'mm',
                    sweep_index: sweepIndex,
                  },
                }
              : {}),
          };
          await uploadUnifiedSweep(payload, file, fileKey);
        } catch {
          failed.push(fileKey);
        }
      }
      if (failed.length > 0) {
        messageI18n.warning('upload.partialResult', {
          uploaded: missingCount - failed.length,
          failed: failed.length,
        });
      } else {
        messageI18n.success('upload.complete');
        onSuccess?.();
        onClose();
      }
    } finally {
      setUploading(false);
    }
  };

  if (!session) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-title-page-color-100 dark:text-white">
            {t('uploadComplete.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-700"
          >
            ×
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-grow">
          {hasMultipleSeries && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">{t('uploadComplete.completeOneSeries')}</p>
              <div className="flex flex-wrap gap-2">
                {allSessions.map((s, idx) => (
                  <button
                    key={s.serie_instance_uid}
                    type="button"
                    onClick={() => { onSelectSession?.(idx); setSelectedFiles([]); }}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      idx === selectedIndex
                        ? 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-200'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {t('uploadComplete.seriesMissing', {
                      index: idx + 1,
                      count: s.missing_keys.length,
                    })}
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="text-sm text-slate-600 dark:text-gray-400 mb-4">
            {hasMultipleSeries ? t('uploadComplete.seriesHasMissing') : t('uploadComplete.examHasMissing')}{' '}
            <strong>{missingCount}</strong>{' '}
            {t('uploadComplete.missingSweepDescription', { count: missingCount })}
          </p>
          <label className="block">
            <span className="text-sm font-medium text-title-page-color-300 dark:text-gray-400 block mb-2">
              {t('uploadComplete.selectFiles', { count: missingCount })}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".dcm,.dicom,.mp4,.webm,.mov,.avi"
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/20 dark:file:text-blue-300"
            />
          </label>
          {selectedFiles.length > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              {t('uploadComplete.selectedFiles', { count: selectedFiles.length })}
              {selectedFiles.length !== missingCount && (
                <span className="text-amber-600 dark:text-amber-400 ml-1">
                  {t('uploadComplete.needCount', { count: missingCount })}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 dark:border-gray-700 gap-3">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            {t('oneStep.patient.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={uploading || !canSubmit}>
            {uploading
              ? t('uploadComplete.uploading')
              : t('uploadComplete.uploadFiles', { count: missingCount })}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
